<script setup lang="ts">
  import { trans, debounce } from '@bv/utils';
  import { ref, computed, watch, useTemplateRef, onBeforeUnmount } from 'vue';
  import { useRouter } from 'vue-router';

  import { contactsApi } from '@/api/webphone/contacts.js';
  import { callCenterKeys } from '@/constants/query-keys/call-center';
  import { ContactDetailsData } from '@/types/api/webPhone/nestedViews/Contact.js';

  import ContactDialog from './AddContact/ContactDialog.vue';
  import ContactCard from './ContactCard.vue';

  defineOptions({ name: 'ContactsSidebar' });

  const router = useRouter();

  const infiniteScrollRef = useTemplateRef<{
    isRefetching: boolean;
    loading: boolean;
    refetch: () => Promise<any>;
    updateItemsOptms: (
      predicate: (item: ContactDetailsData) => boolean,
      updater: (item: ContactDetailsData) => ContactDetailsData,
      apiCall?: () => Promise<unknown>,
    ) => void;
  }>('infiniteScrollRef');

  const addContactStatus = ref<boolean>();
  const activeTab = ref('all');
  const search = ref('');
  const debouncedSearch = ref('');

  const updateDebouncedSearch = debounce((value: string) => {
    debouncedSearch.value = value;
    return value;
  }, 500);

  watch(search, (value) => {
    updateDebouncedSearch(value);
  });

  onBeforeUnmount(() => {
    updateDebouncedSearch.cancel();
  });

  const tabs = computed(() => [
    { title: trans('call_center.contact_category.all'), value: 'all' },
    { title: trans('call_center.contact_category.favorites'), value: 'favorites' },
  ]);

  const selectContact = (contact: ContactDetailsData) => {
    router.push({
      name: 'contact-log-show',
      params: {
        number: contact.phone_number,
      },
      query: {
        id: contact.id,
      },
    });
  };

  const changingFavoriteStatus = ref<Set<string>>(new Set());

  const updateFavoriteStatus = (id: string, status: boolean) => {
    // Optimistic update: instantly reflect the change in the UI
    const apiCall = status ? () => contactsApi.addFavoriteContact(id) : () => contactsApi.deleteFavoriteContact(id);

    infiniteScrollRef.value?.updateItemsOptms(
      (item) => item.id === id,
      (item) => ({ ...item, favorited: status }),
      () => {
        changingFavoriteStatus.value.add(id);
        return apiCall().finally(() => {
          changingFavoriteStatus.value.delete(id);
        });
      },
    );
  };

  const getterConfig = computed(() => {
    return {
      params: {
        'filter[q]': debouncedSearch.value || undefined,
        include: 'tags,favorited',
      },
    };
  });

  const visibleContacts = (contacts: ContactDetailsData[]) => {
    if (activeTab.value === 'favorites' && !search.value) {
      return contacts?.filter((contact) => contact.favorited);
    }
    return contacts;
  };
</script>

<template>
  <div class="grid h-full grid-rows-[auto_auto_auto_1fr] gap-2">
    <bv-btn
      class="h-fit w-full p-2"
      variant="outlined"
      @click="addContactStatus = true"
    >
      <div class="flex items-center gap-2">
        <bv-icon icon="bvi-user-add" />
        <span>{{ trans('call_center.add_to_contacts') }}</span>
      </div>
    </bv-btn>
    <bv-search-input
      v-model="search"
      class="w-full"
    />
    <bv-tabs
      v-model="activeTab"
      class="flex w-full items-center"
      :tabs
    />
    <bv-infinite-scroll
      ref="infiniteScrollRef"
      v-slot="{ items }"
      :cache-key="callCenterKeys.webPhone.contacts()"
      class="-me-3 grid scrollbar-gutter-stable content-start gap-4 overflow-y-auto pe-2"
      :class="{ 'opacity-80 transition-opacity duration-200': infiniteScrollRef?.isRefetching }"
      :getter-config
      :items-getter="(config) => contactsApi.paginated(config)"
    >
      <contact-card
        v-for="contact in visibleContacts(items)"
        :key="contact.id"
        :changing-favorite-status="changingFavoriteStatus.has(contact.id)"
        :contact="contact"
        @add-favorite="updateFavoriteStatus(contact.id, true)"
        @click="selectContact(contact)"
        @remove-favorite="updateFavoriteStatus(contact.id, false)"
      />
    </bv-infinite-scroll>
  </div>

  <contact-dialog
    v-model="addContactStatus"
    @contact-added="infiniteScrollRef?.refetch()"
  />
</template>
